import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getStripePaymentStatus } from '@/lib/api';

export default function CheckoutSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      if (attempts >= 10) {
        setStatus('timeout');
        return;
      }

      try {
        const res = await getStripePaymentStatus(sessionId);
        if (res.data.payment_status === 'paid') {
          setStatus('success');
        } else {
          setAttempts(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        setAttempts(prev => prev + 1);
        setTimeout(pollStatus, 2000);
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin mx-auto mb-4 text-stone-900" />
          <h2 className="text-2xl font-medium mb-2">Processing Payment</h2>
          <p className="text-stone-600">Please wait while we confirm your payment...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" data-testid="success-page">
        <div className="text-center max-w-md">
          <div className="mb-6 flex justify-center">
            <div className="rounded-full bg-green-100 p-6">
              <Check className="h-16 w-16 text-green-600" />
            </div>
          </div>
          <h1 className="text-4xl font-medium mb-4">Order Confirmed!</h1>
          <p className="text-lg text-stone-600 mb-8 leading-relaxed">
            Thank you for your purchase. Your order has been confirmed and will be shipped soon.
          </p>
          <div className="flex flex-col gap-4">
            <Link to="/orders">
              <Button className="w-full btn-primary">View Orders</Button>
            </Link>
            <Link to="/shop">
              <Button variant="outline" className="w-full">Continue Shopping</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <h1 className="text-4xl font-medium mb-4">Payment Processing</h1>
        <p className="text-lg text-stone-600 mb-8">
          We&apos;re still processing your payment. Please check your order status in a few minutes.
        </p>
        <Link to="/orders">
          <Button className="btn-primary">View Orders</Button>
        </Link>
      </div>
    </div>
  );
}
